import React, { useState } from 'react'
import {
  toMajorUnits,
  trackAddToCart,
  trackRemoveFromCart,
} from '@src/analytics/trackingService'
import { useTranslation } from 'react-i18next'

import { AllergenTags } from '@src/components/AllergenTags/AllergenTags'
import {
  CouponContainer,
  StyledCouponContainer,
} from '@src/components/CheckoutModal/Checkout/OrderSummary/OrderSummary.styles'
import { UncontrolledTextInput } from '@src/components/Inputs/TextInput/TextInput'
import { ItemCounter } from '@src/components/ItemCounter/ItemCounter'
import { SpicyChiliSVG } from '@src/components/SVGS/SpicyChiliSVG'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { BasketItemWithPrices } from '@src/hooks/useTotals/useBasketTotals'

import {
  AllergenContainer,
  Container,
  CounterContainer,
  EditButton,
  EditSingleNoteContainer,
  InnerTitleContainer,
  ItemName,
  ItemPrice,
  LeftContainer,
  OptionItemContainer,
  OuterContainer,
  SingleItemNote,
  SingleItemNoteOuterContainer,
  StyledCancelSVG,
  TitleContainer,
} from './BasketItem.styles'
import { OptionItem } from './OptionItem'
import { StyledBasketBoostSVG } from '@src/components/CheckoutModal/Checkout/AddOnItems/AddOnItems.styles'
import { useTheme } from 'styled-components'

type SingleItemProps = {
  basketItemWithPrices: BasketItemWithPrices
  allowSingleItemOrderNotes: boolean
  effectiveMaxQuantity: number | null
  updateQuantity: (newQuantity: number) => void
}

export const BasketItem: React.FC<SingleItemProps> = ({
  basketItemWithPrices,
  allowSingleItemOrderNotes,
  effectiveMaxQuantity,
  updateQuantity,
}) => {
  const { patchItem } = useBasketItems()
  const marketplace = useMarketplace()
  const theme = useTheme()
  const { basketItem, outletMenuItem } = basketItemWithPrices
  const { id: basketItemId, singleItemNotes } = basketItem
  const { allergens, spiceLevel, name, price, ageRestricted, menuItemId } =
    outletMenuItem
  const { t } = useTranslation('checkout')
  const formatCurrency = useFormatCurrency()

  const [isEditingSingleNote, setIsEditingSinglenote] = useState(false)
  const [note, setNote] = useState(singleItemNotes)

  const handleNoteAction = (
    action: 'save' | 'remove' | 'edit',
    value?: string
  ) => {
    if (action === 'edit') {
      setNote(value || '')
    } else if (action === 'save') {
      patchItem({
        basketItemId,
        patchData: { singleItemNotes: note },
      })
      setIsEditingSinglenote(false)
    } else if (action === 'remove') {
      patchItem({
        basketItemId,
        patchData: { singleItemNotes: '' },
      })
      setNote('')
    }
  }

  const handleAddQuantity = () => {
    updateQuantity(basketItemWithPrices.basketItem.quantity + 1)
    trackAddToCart({
      value: toMajorUnits(price),
      currency: marketplace.country.currency.iso4217,
      affiliation: name,
      items: [
        { itemId: menuItemId, itemName: name, price: toMajorUnits(price) },
      ],
    })
  }

  const isMaxQuantityReached =
    effectiveMaxQuantity !== null &&
    basketItemWithPrices.basketItem.quantity >= effectiveMaxQuantity

  return (
    <OuterContainer>
      <Container data-test-id="basket-item-container-main">
        <LeftContainer>
          <TitleContainer data-test-id="basket-item-container-title">
            <InnerTitleContainer>
              <ItemName data-test-id="basket-item-text-name">{name}</ItemName>
              {spiceLevel > 0 &&
                Array.from({ length: spiceLevel }).map((_, index) => (
                  <SpicyChiliSVG
                    width={15}
                    height={15}
                    key={index}
                    data-test-id={`basket-item-icon-spice-${index + 1}`}
                  />
                ))}
            </InnerTitleContainer>
            <ItemPrice data-test-id="basket-item-text-price">
              {basketItemWithPrices.isAddOnItem ? (
                <CouponContainer>
                  <StyledCouponContainer>
                    <StyledBasketBoostSVG
                      fill={theme.colors.brandHighlight}
                      isCheckout
                      id="offer-item"
                      title={t('order_summary.offer_item')}
                      desc={t('order_summary.offer_item_desc')}
                      data-test-id="basket-item-icon-addonitem"
                    />
                  </StyledCouponContainer>
                  {formatCurrency(basketItemWithPrices.totalPrice)}
                </CouponContainer>
              ) : (
                formatCurrency(basketItemWithPrices.totalPrice)
              )}
            </ItemPrice>
          </TitleContainer>
          {allergens && (
            <AllergenContainer data-test-id="basket-item-container-allergens">
              <AllergenTags
                iconView
                allergens={allergens}
                ageRestricted={!!ageRestricted}
              />
            </AllergenContainer>
          )}
          {basketItemWithPrices.selectedOptionItems.length > 0 && (
            <OptionItemContainer data-test-id="basket-item-container-options">
              {basketItemWithPrices.selectedOptionItems.map(optionItem => (
                <OptionItem
                  key={optionItem.id}
                  optionItem={optionItem}
                  quantity={basketItemWithPrices.basketItem.quantity}
                />
              ))}
            </OptionItemContainer>
          )}
        </LeftContainer>

        <CounterContainer data-test-id="basket-item-container-counter">
          <ItemCounter
            price={price}
            count={basketItemWithPrices.basketItem.quantity}
            handleAdd={handleAddQuantity}
            handleRemove={() => {
              updateQuantity(basketItemWithPrices.basketItem.quantity - 1)
              trackRemoveFromCart({
                value: toMajorUnits(price),
                currency: marketplace.country.currency.iso4217,
                affiliation: name,
                items: [
                  {
                    itemId: menuItemId,
                    itemName: name,
                    price: toMajorUnits(price),
                  },
                ],
              })
            }}
            allowAddItem={!isMaxQuantityReached}
            addItemText={
              isMaxQuantityReached
                ? t('reached_max_purchase_quantity')
                : undefined
            }
            dataTestId="basket-item-counter-quantity"
          />
        </CounterContainer>
      </Container>

      {/* Only if single item notes are allowed */}
      {allowSingleItemOrderNotes && singleItemNotes ? (
        <SingleItemNoteOuterContainer data-test-id="basket-item-container-singlenote">
          {isEditingSingleNote ? (
            <EditSingleNoteContainer data-test-id="basket-item-container-editnote">
              <UncontrolledTextInput
                value={note}
                onChange={(event: { target: { value: string } }) => {
                  const { value } = event.target
                  handleNoteAction('edit', value)
                }}
                onBlur={() => {
                  handleNoteAction('save')
                  setIsEditingSinglenote(false)
                }}
                label={t('edit_single_item_note')}
                touched
                required={false}
                name="editSpecialInstructions"
                focusOnLoad
                dataTestId="basket-item-input-editnote"
              />
            </EditSingleNoteContainer>
          ) : (
            <>
              <SingleItemNote data-test-id="basket-item-container-notetext">
                <StyledCancelSVG
                  title={t('remove_item_note')}
                  id="remove_note"
                  onClick={() => handleNoteAction('remove')}
                  data-test-id="basket-item-button-removenote"
                />
                <p data-test-id="basket-item-text-note">{note}</p>
              </SingleItemNote>
              <EditButton
                onClick={() => setIsEditingSinglenote(true)}
                data-test-id="basket-item-button-editnote"
              >
                {t('edit')}
              </EditButton>
            </>
          )}
        </SingleItemNoteOuterContainer>
      ) : null}
    </OuterContainer>
  )
}
